import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ExcelJS from "exceljs";
import { Helmet } from "react-helmet-async";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../../DesignSystem/Sidebar";
import Breadcrumb from "../../DesignSystem/Breadcrumb/Breadcrumb";
import PrimaryButton from "../../DesignSystem/Buttons/PrimaryButton";
import SearchField from "../../DesignSystem/Inputs/SearchField";
import Tooltip from "../../DesignSystem/Tooltip/Tooltip";
import { useUserPermissions } from "../../SignIn/UserPermissionsContext";
import { APP_ROUTES } from "../../../constants/appRoutes";
import { canAccessDepartmentRoute } from "../../Departments/utils/departmentConfig";
import { ReactComponent as InfoIcon } from "../../../assets/icons/information.svg";
import { ReactComponent as SortIcon } from "../../../assets/icons/paraplanningDashboard/Sort.svg";
import { RATE_FIELDS, formatRateValue } from "../../Adviser/Tabs/Rates/rateFields";
import { loadAdviserRateRows } from "../../Adviser/Tabs/Rates/ratesService";
import "../../../css/Adviser/LicenseCertificatesSummary.css";

const DISPLAY_RATE_FIELDS = RATE_FIELDS.filter(
  (field) => field.key !== "monthlyFees",
);
const DEFAULT_SORT = { key: "practice", direction: "asc" };
const listStateByLocation = new Map();

const downloadRatesExcel = async (rows, allRows = rows) => {
  const adviserNameByEmail = new Map(
    allRows
      .filter((row) => row.adviserEmail)
      .map((row) => [
        String(row.adviserEmail).trim().toLowerCase(),
        row.adviser || row.adviserEmail,
      ]),
  );
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Rosemount Staff Portal";
  workbook.created = new Date();

  const sheet = workbook.addWorksheet("IFA Rates", {
    views: [{ state: "frozen", ySplit: 1, showGridLines: false }],
  });
  sheet.columns = [
    { header: "Practice", key: "practice", width: 34 },
    { header: "Adviser", key: "adviser", width: 28 },
    { header: "Adviser Email", key: "adviserEmail", width: 36 },
    ...DISPLAY_RATE_FIELDS.map((field) => ({
      header: field.label,
      key: field.key,
      width: Math.max(field.label.length + 4, 14),
    })),
    { header: "Monthly Fees", key: "monthlyFees", width: 18 },
    { header: "Paid By", key: "paidBy", width: 28 },
    {
      header: "Total Monthly Fees Charged",
      key: "totalMonthlyFeesCharged",
      width: 28,
    },
  ];

  rows.forEach((row) => {
    const paidByEmail = String(row.paidBy || "").trim().toLowerCase();
    const adviserEmail = String(row.adviserEmail || "").trim().toLowerCase();
    const paidBy =
      !paidByEmail || paidByEmail === adviserEmail
        ? "Self"
        : adviserNameByEmail.get(paidByEmail) || row.paidBy;
    const monthlyFees = Number(row.monthlyFees);
    const values = {
      practice: row.practice || "",
      adviser: row.adviser || "",
      adviserEmail: row.adviserEmail || "",
      monthlyFees:
        row.monthlyFees === "" || !Number.isFinite(monthlyFees)
          ? null
          : monthlyFees,
      paidBy,
      totalMonthlyFeesCharged:
        row.totalMonthlyFeesCharged === ""
          ? null
          : Number(row.totalMonthlyFeesCharged),
    };

    DISPLAY_RATE_FIELDS.forEach((field) => {
      const value = row[field.key];
      if (value === "" || value === null || value === undefined) {
        values[field.key] = null;
        return;
      }

      const number = Number(value);
      values[field.key] =
        field.type === "percentage" && Number.isFinite(number)
          ? number > 1
            ? number / 100
            : number
          : Number.isFinite(number)
            ? number
            : value;
    });

    sheet.addRow(values);
  });

  const header = sheet.getRow(1);
  header.height = 28;
  header.font = { bold: true, color: { argb: "FFFFFFFF" } };
  header.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF081E56" },
  };
  header.alignment = { vertical: "middle" };
  header.eachCell((cell) => {
    cell.border = {
      bottom: { style: "thin", color: { argb: "FFD0D5DD" } },
    };
  });

  DISPLAY_RATE_FIELDS.forEach((field, index) => {
    if (field.type === "percentage") {
      sheet.getColumn(index + 4).numFmt = "0.00%";
    }
  });
  sheet.getColumn(DISPLAY_RATE_FIELDS.length + 4).numFmt = '£#,##0.00';
  sheet.getColumn(DISPLAY_RATE_FIELDS.length + 6).numFmt = '£#,##0.00';
  sheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: sheet.columnCount },
  };

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `IFA-Rates-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const formatCurrency = (value) => {
  if (value === "" || value === null || value === undefined) return "-";

  const number = Number(value);
  return Number.isFinite(number) ? `£${number.toFixed(2)}` : "-";
};

const MonthlyFeesBreakdown = ({ row }) => {
  const payingFor = Array.isArray(row.payingFor) ? row.payingFor : [];
  const normalisedPaidBy = String(row.paidBy || "").trim().toLowerCase();
  const normalisedAdviserEmail = String(row.adviserEmail || "")
    .trim()
    .toLowerCase();
  const ownMonthlyFee =
    !normalisedPaidBy || normalisedPaidBy === normalisedAdviserEmail
      ? row.monthlyFees
      : 0;

  return (
    <span
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto",
        gap: "6px 12px",
        textAlign: "left",
      }}
    >
      <span className="FP_Help_Text_Semibold white-text">Adviser</span>
      <span className="FP_Help_Text_Semibold white-text">Monthly Fee</span>

      <span>{row.adviser}</span>
      <span>{formatCurrency(ownMonthlyFee)}</span>

      {payingFor.map((person) => (
        <React.Fragment key={person.adviserId || person.email}>
          <span>{person.name || "-"}</span>
          <span>{formatCurrency(person.monthlyFee)}</span>
        </React.Fragment>
      ))}

      <span
        className="FP_Help_Text_Semibold white-text"
        style={{
          borderTop: "1px solid rgba(255, 255, 255, 0.3)",
          paddingTop: 6,
        }}
      >
        Total
      </span>
      <span
        className="FP_Help_Text_Semibold white-text"
        style={{
          borderTop: "1px solid rgba(255, 255, 255, 0.3)",
          paddingTop: 6,
        }}
      >
        {formatCurrency(row.totalMonthlyFeesCharged)}
      </span>
    </span>
  );
};

const SortableHeader = ({ label, active }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
    }}
  >
    <span className="FP_Help_Text_Semibold">{label}</span>
    <span
      className="lcSortIcon"
      style={{ opacity: active ? 1 : 0.65 }}
      aria-hidden
    >
      <SortIcon width={14} height={14} />
    </span>
  </div>
);

export default function RatesSummaryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userPermissions = [] } = useUserPermissions();
  const savedListState = listStateByLocation.get(location.key);
  const tableWrapRef = useRef(null);
  const hasRestoredScroll = useRef(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState(savedListState?.query || "");
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [sortConfig, setSortConfig] = useState(
    savedListState?.sortConfig || DEFAULT_SORT,
  );
  const canAccessRates = canAccessDepartmentRoute(
    userPermissions,
    APP_ROUTES.IFA_RATES,
  );

  useEffect(() => {
    loadAdviserRateRows()
      .then(setRows)
      .catch((reason) => setError(reason.message || "Rates could not be loaded."))
      .finally(() => setLoading(false));
  }, []);

  const filteredRows = useMemo(() => {
    const term = query.trim().toLowerCase();
    const matchingRows = term
      ? rows.filter((row) =>
          `${row.practice} ${row.adviser}`.toLowerCase().includes(term),
        )
      : rows;

    return [...matchingRows].sort((first, second) => {
      const firstValue = first[sortConfig.key];
      const secondValue = second[sortConfig.key];
      const firstEmpty =
        firstValue === "" || firstValue === null || firstValue === undefined;
      const secondEmpty =
        secondValue === "" ||
        secondValue === null ||
        secondValue === undefined;

      if (firstEmpty && secondEmpty) return 0;
      if (firstEmpty) return 1;
      if (secondEmpty) return -1;

      const firstNumber = Number(firstValue);
      const secondNumber = Number(secondValue);
      const comparison =
        Number.isFinite(firstNumber) && Number.isFinite(secondNumber)
          ? firstNumber - secondNumber
          : String(firstValue).localeCompare(String(secondValue), "en", {
              sensitivity: "base",
            });

      if (comparison === 0 && sortConfig.key === "practice") {
        return String(first.adviser || "").localeCompare(
          String(second.adviser || ""),
          "en",
          { sensitivity: "base" },
        );
      }

      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [query, rows, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getAriaSort = (key) => {
    if (sortConfig.key !== key) return "none";
    return sortConfig.direction === "asc" ? "ascending" : "descending";
  };

  useLayoutEffect(() => {
    if (loading || hasRestoredScroll.current || !savedListState) return;

    const tableWrap = tableWrapRef.current;
    if (!tableWrap) return;

    tableWrap.scrollTop = savedListState.scrollTop;
    tableWrap.scrollLeft = savedListState.scrollLeft;
    hasRestoredScroll.current = true;
    listStateByLocation.delete(location.key);
  }, [loading, location.key, savedListState]);

  const openRatesEditor = (row) => {
    const tableWrap = tableWrapRef.current;
    listStateByLocation.set(location.key, {
      query,
      sortConfig,
      scrollTop: tableWrap?.scrollTop || 0,
      scrollLeft: tableWrap?.scrollLeft || 0,
    });

    navigate(
      `/advisers/${encodeURIComponent(row.adviserId)}/rates/edit`,
      {
        state: {
          adviserKey: row.adviserId,
          adviserEmail: row.adviserEmail || "",
        },
      },
    );
  };

  const handleDownloadExcel = async () => {
    setDownloading(true);
    setError("");
    try {
      await downloadRatesExcel(filteredRows, rows);
    } catch (downloadError) {
      setError(downloadError.message || "Could not download the IFA rates.");
    } finally {
      setDownloading(false);
    }
  };

  if (!canAccessRates) {
    return <Navigate to="/Department/Finance" replace />;
  }

  return (
    <div className="main-content lcSummaryShell">
      <Helmet>
        <title>IFA Rates</title>
      </Helmet>
      <Sidebar />
      <div className="Main-Container ">
        <main className="column gap-s">
          <Breadcrumb
            items={[
              { label: "Finance", to: "/Department/Finance" },
              { label: "IFA Rates" },
            ]}
          />
          <div className="lcSummaryHeader">
            <div className="lcSummaryTitleWrap">
              <div>
                <h3 className="FP_H3">IFA Rates</h3>
                {/* <p className="Content-Small muted">
                  Review rates for all advisers. Select an adviser to edit their
                  record.
                </p> */}
              </div>
            </div>
            <div className="lcSummaryActions">
              <SearchField
                label=""
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Adviser or practice"
              />
              {/* <PrimaryButton
                label="Upload Excel"
                onClick={() => navigate("/compliance/ifa-rates/bulk-upload")}
                disabled={loading || downloading}
              /> */}
              <PrimaryButton
                label={downloading ? "Downloading..." : "Download Excel"}
                onClick={handleDownloadExcel}
                disabled={loading || downloading || filteredRows.length === 0}
              />
            </div>
          </div>

          {error ? <div className="adviserLicenseError">{error}</div> : null}
          <div className="lcTableWrap" ref={tableWrapRef}>
            <table className="lcTable ifaRatesTable">
              <thead>
                <tr>
                  <th
                    className="lcPinnedColumn lcSortableHead"
                    onClick={() => handleSort("practice")}
                    aria-sort={getAriaSort("practice")}
                    title="Sort by practice"
                    style={{
                      left: 0,
                      width: 260,
                      minWidth: 260,
                      maxWidth: 260,
                    }}
                  >
                    <SortableHeader
                      label="Practice"
                      active={sortConfig.key === "practice"}
                    />
                  </th>
                  <th
                    className="lcPinnedColumn lcPinnedColumnLeft lcSortableHead"
                    onClick={() => handleSort("adviser")}
                    aria-sort={getAriaSort("adviser")}
                    title="Sort by adviser"
                    style={{
                      left: 260,
                      width: 200,
                      minWidth: 200,
                      maxWidth: 200,
                    }}
                  >
                    <SortableHeader
                      label="Adviser"
                      active={sortConfig.key === "adviser"}
                    />
                  </th>
                  {DISPLAY_RATE_FIELDS.map((field) => (
                    <th
                      key={field.key}
                      className="lcSortableHead"
                      onClick={() => handleSort(field.key)}
                      aria-sort={getAriaSort(field.key)}
                      title={`Sort by ${field.label}`}
                    >
                      <SortableHeader
                        label={field.label}
                        active={sortConfig.key === field.key}
                      />
                    </th>
                  ))}
                  <th
                    className="lcSortableHead"
                    onClick={() => handleSort("totalMonthlyFeesCharged")}
                    aria-sort={getAriaSort("totalMonthlyFeesCharged")}
                    title="Sort by total monthly fees charged"
                  >
                    <SortableHeader
                      label="Total Monthly Fees Charged"
                      active={
                        sortConfig.key === "totalMonthlyFeesCharged"
                      }
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {!loading && filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={DISPLAY_RATE_FIELDS.length + 3}>
                      No adviser rates found.
                    </td>
                  </tr>
                ) : null}
                {filteredRows.map((row) => (
                  <tr
                    key={row.adviserId}
                    onClick={() => openRatesEditor(row)}
                  >
                    <td
                      className="lcPinnedColumn lcWrappedCell FP_Help_Text text-black"
                      style={{
                        left: 0,
                        width: 260,
                        minWidth: 260,
                        maxWidth: 260,
                      }}
                    >
                      {row.practice || "-"}
                    </td>
                    <td
                      className="lcPinnedColumn lcPinnedColumnLeft lcWrappedCell"
                      style={{
                        left: 260,
                        width: 200,
                        minWidth: 200,
                        maxWidth: 200,
                      }}
                    >
                      <span className="lcClickableTextCell lcWrappedCell FP_Help_Text primary-text">
                        {row.adviser}
                      </span>
                    </td>
                    {DISPLAY_RATE_FIELDS.map((field) => (
                      <td
                        key={field.key}
                        className="FP_Help_Text text-black text-align-right"
                      >
                        {formatRateValue(row[field.key], field.type)}
                      </td>
                    ))}
                    <td className="FP_Help_Text text-black text-align-right">
                      <div
                        className="lcStatusCell"
                        style={{ justifyContent: "flex-end" }}
                      >
                        <span>
                          {formatCurrency(row.totalMonthlyFeesCharged)}
                        </span>
                        <button
                          type="button"
                          className="table-row-action"
                          onClick={(event) => event.stopPropagation()}
                          aria-label={`Monthly fee breakdown for ${row.adviser}`}
                        >
                          <Tooltip
                            position="left"
                            content={<MonthlyFeesBreakdown row={row} />}
                          >
                            <InfoIcon
                              className="lcInfoIcon"
                              aria-hidden
                            />
                          </Tooltip>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {loading ? (
                  <tr>
                    <td colSpan={DISPLAY_RATE_FIELDS.length + 3}>
                      Loading rates…
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </main>
      </div>
    </div>
  );
}
